import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import AppShell from "../components/AppShell";
import CommandBar from "../components/CommandBar";
import LeadCard from "../components/LeadCard";
import LeadDrawer from "../components/LeadDrawer";
import { api } from "../lib/api";
import { STAGES } from "../lib/format";
import { toast } from "sonner";
import { Flame, Archive, CaretDown, CaretRight, ShieldCheck } from "@phosphor-icons/react";
import RevenueHeader from "../components/RevenueHeader";
import BatchDataStatusBanner from "../components/BatchDataStatusBanner";

export default function Dashboard() {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [blasting, setBlasting] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [liveOnly, setLiveOnly] = useState(true);
  const prevHotRef = useRef(0);

  const load = useCallback(async () => {
    try {
      const [l, s] = await Promise.all([
        api.get("/leads", { params: liveOnly ? { live_only: true } : {} }),
        api.get("/stats"),
      ]);
      setLeads(l.data); setStats(s.data);
      return s.data;
    } catch { toast.error("Failed to load"); }
  }, [liveOnly]);

  useEffect(() => { load(); }, [load]);

  // Real-time polling for HOT deal arrivals
  useEffect(() => {
    const t = setInterval(async () => {
      const s = await load();
      const newHot = s?.counts?.hot_deals ?? 0;
      if (newHot > prevHotRef.current && prevHotRef.current !== 0) {
        toast.success(`🔥 New HOT DEAL — seller said YES. Handoff sent to your inbox.`, { duration: 8000 });
        try { new Audio("data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA=").play(); } catch {}
      }
      prevHotRef.current = newHot;
    }, 15000);
    return () => clearInterval(t);
  }, [load]);

  const runScan = async () => {
    setScanning(true);
    try {
      const r = await api.post("/scan/run");
      const auditNote = r.data.audited_out ? ` · ${r.data.audited_out} audited-out` : "";
      toast.success(`Scan complete · ${r.data.inserted} new leads (of ${r.data.total})${auditNote}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scan failed");
    } finally { setScanning(false); }
  };

  const blast = async () => {
    setBlasting(true);
    try {
      const r = await api.post("/outreach/blast", { limit: 10, require_email: true });
      const { attempted, sent, mock_sent, failed } = r.data;
      if (attempted === 0) toast.info("No eligible New Leads with an owner email on file.");
      else if (sent > 0) toast.success(`Blast complete · ${sent} live-sent · ${mock_sent} demo-skipped · ${failed} failed`);
      else toast.warning(`Blast complete · 0 live-sent · ${mock_sent} demo-skipped · ${failed} failed (demo recipients skipped — refill BatchData for real emails)`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Blast failed");
    } finally { setBlasting(false); }
  };

  const grouped = useMemo(() => {
    const g = { new_leads: [], offer_sent: [], under_review: [], hot_deals: [], archived: [] };
    leads.forEach(l => { (g[l.stage] || g.new_leads).push(l); });
    return g;
  }, [leads]);

  const hotCount = grouped.hot_deals.length;
  const archivedCount = grouped.archived.length;
  const newLeadsWithEmail = grouped.new_leads.filter(l => l.owner_email).length;

  return (
    <AppShell header={<CommandBar stats={stats} onScan={runScan} scanning={scanning} onBlast={blast} blasting={blasting} newLeadsWithEmail={newLeadsWithEmail} />}>
      <BatchDataStatusBanner />
      <div className="px-6 pt-4">
        <RevenueHeader revenue={stats?.revenue} counts={stats?.counts} />
      </div>

      {hotCount > 0 && (
        <div className="mx-6 border border-emerald-500/40 bg-emerald-500/8 px-4 py-3 rounded-sm flex items-center gap-3" data-testid="hot-banner">
          <Flame size={18} weight="fill" className="text-emerald-400" />
          <div className="text-sm flex-1">
            <span className="font-semibold text-emerald-300">{hotCount} HOT DEAL{hotCount > 1 ? "S" : ""}</span>
            <span className="text-zinc-400"> ready to sign — sellers have said Yes. Handoff already sent to your inbox.</span>
          </div>
        </div>
      )}

      <div className="p-6 pt-0">
        <div className="flex items-center justify-between mb-3">
          <label className="flex items-center gap-2 text-xs mono uppercase tracking-[0.15em] cursor-pointer select-none">
            <input type="checkbox" checked={liveOnly} onChange={(e) => setLiveOnly(e.target.checked)} data-testid="live-only-toggle" />
            <ShieldCheck size={14} className={liveOnly ? "text-emerald-400" : "text-zinc-500"} />
            <span className={liveOnly ? "text-emerald-300" : "text-zinc-500"}>Live Resend-sent leads only</span>
          </label>
          {liveOnly && <div className="text-[10px] mono text-zinc-500">Filter: at least one email delivered with status=sent</div>}
        </div>

        {leads.length === 0 && stats && (
          <div className="mb-4 border border-zinc-800/80 bg-[#0b0b0e] rounded-sm p-6 text-center" data-testid="empty-state">
            <div className="overline mb-2">Clean Sheet</div>
            <div className="text-lg font-semibold mb-1">No leads on the board yet</div>
            <div className="text-sm text-zinc-500">
              The 24/7 scanner is armed. Real leads appear here the moment BatchData returns them.
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4" data-testid="kanban-board">
          {STAGES.map(col => (
            <div key={col.key} className="flex flex-col border border-zinc-800/80 rounded-sm bg-[#0b0b0e]" data-testid={`kanban-col-${col.key}`}>
              <div className="px-3.5 py-2.5 border-b border-zinc-800 flex items-center justify-between sticky top-0 bg-[#0b0b0e]">
                <div className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: col.accent }} />
                  <span className="mono text-[11px] uppercase tracking-[0.2em]" style={{ color: col.accent }}>{col.label}</span>
                </div>
                <span className="mono text-[11px] text-zinc-500">{grouped[col.key].length}</span>
              </div>
              <div className="flex-1 p-2.5 space-y-2.5 overflow-y-auto scroll-y stagger" style={{ maxHeight: "calc(100vh - 260px)" }}>
                {grouped[col.key].length === 0 && (
                  <div className="text-xs text-zinc-600 mono px-2 py-8 text-center border border-dashed border-zinc-800 rounded-sm">
                    Empty
                  </div>
                )}
                {grouped[col.key].map(l => (
                  <LeadCard key={l.id} lead={l} onClick={() => setOpenId(l.id)} />
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Archived (Lead Auditor rejects live here) */}
        <div className="mt-4 border border-zinc-800/80 rounded-sm bg-[#0a0a0d]" data-testid="archived-drawer">
          <button
            onClick={() => setShowArchived(v => !v)}
            data-testid="toggle-archived"
            className="w-full flex items-center justify-between px-3.5 py-2.5 hover:bg-zinc-900 transition-colors"
          >
            <div className="flex items-center gap-2">
              {showArchived ? <CaretDown size={14} /> : <CaretRight size={14} />}
              <Archive size={14} className="text-zinc-500" />
              <span className="mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">Archived</span>
              <span className="mono text-[11px] text-zinc-600">· {archivedCount}</span>
            </div>
            <span className="mono text-[10px] text-zinc-600 hidden sm:block">
              declined by seller · silent
            </span>
          </button>
          {showArchived && (
            <div className="border-t border-zinc-800 p-2.5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5">
              {archivedCount === 0 ? (
                <div className="text-xs text-zinc-600 mono px-2 py-6 text-center border border-dashed border-zinc-800 rounded-sm sm:col-span-2 lg:col-span-4">
                  Nothing archived yet.
                </div>
              ) : (
                grouped.archived.map(l => (
                  <LeadCard key={l.id} lead={l} onClick={() => setOpenId(l.id)} />
                ))
              )}
            </div>
          )}
        </div>
      </div>

      {openId && <LeadDrawer leadId={openId} onClose={() => setOpenId(null)} onUpdated={load} />}
    </AppShell>
  );
}
